import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Car, Home, Shield, ChevronRight, CheckCircle, TrendingUp, Users, Award, Phone, Mail, MapPin } from 'lucide-react';

const BethFinanceLanding = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('auto');

  const loanTypes = {
    auto: {
      title: 'Auto Loans',
      icon: Car,
      fee: '$49',
      description: 'Get pre-approved for your next vehicle with credit enhancement',
      features: ['Competitive rates from multiple lenders', 'Same-day decisions', 'New & used vehicles', 'Refinancing options']
    },
    mortgage: {
      title: 'Mortgage Loans',
      icon: Home,
      fee: '$199',
      description: 'Improve your mortgage approval odds with our credit enhancement',
      features: ['Purchase & refinance', 'FHA, VA, Conventional', 'First-time buyer programs', 'Competitive rates']
    }
  };

  const currentLoan = loanTypes[activeTab];
  const LoanIcon = currentLoan.icon;

  return (
    <div className="min-h-screen bg-white">
      {/* Header - Bank of America Style */}
      <header className="bg-[#012169] text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/beth-finance" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-[#E31837] rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-xl">B</span>
              </div>
              <div className="flex flex-col">
                <span className="text-xl font-bold tracking-tight">BETH Finance</span>
                <span className="text-xs text-gray-300">Credit Enhancement Solutions</span>
              </div>
            </Link>

            {/* Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#how-it-works" className="text-sm hover:text-[#E31837] transition-colors">How It Works</a>
              <a href="#loan-types" className="text-sm hover:text-[#E31837] transition-colors">Loan Types</a>
              <a href="#why-us" className="text-sm hover:text-[#E31837] transition-colors">Why BETH Finance</a>
              <a href="#contact" className="text-sm hover:text-[#E31837] transition-colors">Contact</a>
            </nav>

            {/* Auth Buttons */}
            <div className="flex items-center space-x-4">
              <Link 
                to="/beth-finance/login" 
                className="text-sm hover:text-[#E31837] transition-colors"
              >
                Sign In
              </Link>
              <Link 
                to="/beth-finance/signup" 
                className="bg-[#E31837] hover:bg-[#c4142f] px-4 py-2 rounded text-sm font-medium transition-colors"
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#012169] via-[#01337d] to-[#014091] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-6">
                Boost Your Loan Approval Odds with <span className="text-[#E31837]">Credit Enhancement</span>
              </h1>
              <p className="text-xl text-gray-200 mb-8">
                BETH Finance helps you secure better loan terms by enhancing your credit profile before submitting applications to our network of trusted lenders.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={() => navigate('/beth-finance/signup')}
                  className="bg-[#E31837] hover:bg-[#c4142f] px-8 py-4 rounded-lg font-semibold text-lg flex items-center justify-center transition-all transform hover:scale-105"
                >
                  Apply Now <ChevronRight className="ml-2 w-5 h-5" />
                </button>
                <a 
                  href="#how-it-works"
                  className="border-2 border-white hover:bg-white hover:text-[#012169] px-8 py-4 rounded-lg font-semibold text-lg flex items-center justify-center transition-all"
                >
                  Learn More
                </a>
              </div>
              <div className="mt-10 flex items-center space-x-8">
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-400 mr-2" />
                  <span className="text-sm">No Credit Impact</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-400 mr-2" />
                  <span className="text-sm">Fast Decisions</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-400 mr-2" />
                  <span className="text-sm">Multiple Lenders</span>
                </div>
              </div>
            </div>
            
            {/* Loan Type Selector Card */}
            <div className="bg-white rounded-2xl shadow-2xl p-6 text-gray-900">
              <h3 className="text-xl font-bold mb-4 text-[#012169]">Select Your Loan Type</h3>
              
              {/* Tabs */}
              <div className="flex mb-6 bg-gray-100 rounded-lg p-1">
                <button 
                  onClick={() => setActiveTab('auto')}
                  className={`flex-1 py-3 rounded-md font-medium transition-all ${activeTab === 'auto' ? 'bg-[#012169] text-white' : 'text-gray-600 hover:text-[#012169]'}`}
                >
                  <Car className="w-5 h-5 inline mr-2" />
                  Auto Loan
                </button>
                <button 
                  onClick={() => setActiveTab('mortgage')}
                  className={`flex-1 py-3 rounded-md font-medium transition-all ${activeTab === 'mortgage' ? 'bg-[#012169] text-white' : 'text-gray-600 hover:text-[#012169]'}`}
                >
                  <Home className="w-5 h-5 inline mr-2" />
                  Mortgage
                </button>
              </div>

              {/* Selected Loan Info */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-[#012169] rounded-full flex items-center justify-center mr-4">
                      <LoanIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg">{currentLoan.title}</h4>
                      <p className="text-gray-500 text-sm">Credit Enhancement Fee</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-3xl font-bold text-[#E31837]">{currentLoan.fee}</span>
                    <p className="text-xs text-gray-500">one-time</p>
                  </div>
                </div>
                <p className="text-gray-600 mb-4">{currentLoan.description}</p>
                <ul className="space-y-2">
                  {currentLoan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              <button 
                onClick={() => navigate('/beth-finance/signup')}
                className="w-full bg-[#E31837] hover:bg-[#c4142f] text-white py-4 rounded-lg font-semibold transition-all"
              >
                Start Your Application
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#012169] mb-4">How BETH Finance Works</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Our streamlined process helps you get the best loan terms in just a few simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Create Account', desc: 'Sign up and provide your basic information securely', icon: Users },
              { step: '02', title: 'Purchase Enhancement', desc: 'Select your loan type and pay the one-time fee', icon: Shield },
              { step: '03', title: 'Submit Application', desc: 'Complete your loan application with enhanced credit profile', icon: TrendingUp },
              { step: '04', title: 'Get Approved', desc: 'Receive offers from multiple lenders in our network', icon: Award }
            ].map((item, idx) => (
              <div key={idx} className="relative">
                <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow h-full">
                  <div className="absolute -top-4 -left-4 w-12 h-12 bg-[#E31837] rounded-full flex items-center justify-center text-white font-bold">
                    {item.step}
                  </div>
                  <div className="pt-4">
                    <div className="w-14 h-14 bg-[#012169]/10 rounded-xl flex items-center justify-center mb-4">
                      <item.icon className="w-7 h-7 text-[#012169]" />
                    </div>
                    <h3 className="text-lg font-bold text-[#012169] mb-2">{item.title}</h3>
                    <p className="text-gray-600">{item.desc}</p>
                  </div>
                </div>
                {idx < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 w-8">
                    <ChevronRight className="w-8 h-8 text-gray-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Loan Types Section */}
      <section id="loan-types" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#012169] mb-4">Credit Enhancement Solutions</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Choose the right solution for your financing needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Auto Loan Card */}
            <div className="bg-gradient-to-br from-[#012169] to-[#01337d] rounded-2xl p-8 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -mr-20 -mt-20"></div>
              <Car className="w-16 h-16 mb-6 text-[#E31837]" />
              <h3 className="text-2xl font-bold mb-2">Auto Loan Enhancement</h3>
              <div className="flex items-baseline mb-4">
                <span className="text-4xl font-bold">$49</span>
                <span className="text-gray-300 ml-2">one-time fee</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center"><CheckCircle className="w-5 h-5 mr-3 text-green-400" /> New & used vehicle financing</li>
                <li className="flex items-center"><CheckCircle className="w-5 h-5 mr-3 text-green-400" /> Auto refinancing</li>
                <li className="flex items-center"><CheckCircle className="w-5 h-5 mr-3 text-green-400" /> Multiple lender matching</li>
                <li className="flex items-center"><CheckCircle className="w-5 h-5 mr-3 text-green-400" /> Same-day decisions</li>
              </ul>
              <button 
                onClick={() => navigate('/beth-finance/signup?type=auto')}
                className="w-full bg-[#E31837] hover:bg-[#c4142f] py-3 rounded-lg font-semibold transition-all"
              >
                Apply for Auto Loan
              </button>
            </div>

            {/* Mortgage Card */}
            <div className="bg-gradient-to-br from-[#012169] to-[#01337d] rounded-2xl p-8 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -mr-20 -mt-20"></div>
              <div className="absolute top-4 right-4 bg-[#E31837] px-3 py-1 rounded-full text-xs font-medium">POPULAR</div>
              <Home className="w-16 h-16 mb-6 text-[#E31837]" />
              <h3 className="text-2xl font-bold mb-2">Mortgage Enhancement</h3>
              <div className="flex items-baseline mb-4">
                <span className="text-4xl font-bold">$199</span>
                <span className="text-gray-300 ml-2">one-time fee</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center"><CheckCircle className="w-5 h-5 mr-3 text-green-400" /> Purchase & refinance</li>
                <li className="flex items-center"><CheckCircle className="w-5 h-5 mr-3 text-green-400" /> FHA, VA, Conventional</li>
                <li className="flex items-center"><CheckCircle className="w-5 h-5 mr-3 text-green-400" /> First-time buyer programs</li>
                <li className="flex items-center"><CheckCircle className="w-5 h-5 mr-3 text-green-400" /> Competitive rates</li>
              </ul>
              <button 
                onClick={() => navigate('/beth-finance/signup?type=mortgage')}
                className="w-full bg-[#E31837] hover:bg-[#c4142f] py-3 rounded-lg font-semibold transition-all"
              >
                Apply for Mortgage
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section id="why-us" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#012169] mb-4">Why Choose BETH Finance?</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We're committed to helping you achieve your financial goals
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { 
                title: 'Trusted Lender Network', 
                desc: 'Access to vetted, reputable lenders offering competitive rates and terms',
                stat: '50+',
                statLabel: 'Partner Lenders'
              },
              { 
                title: 'Higher Approval Rates', 
                desc: 'Our credit enhancement solutions significantly improve your chances of approval',
                stat: '87%',
                statLabel: 'Approval Rate'
              },
              { 
                title: 'Fast Processing', 
                desc: 'Get decisions quickly with our streamlined application process',
                stat: '24hr',
                statLabel: 'Avg. Decision Time'
              }
            ].map((item, idx) => (
              <div key={idx} className="bg-white rounded-xl p-8 shadow-lg text-center">
                <div className="text-5xl font-bold text-[#E31837] mb-2">{item.stat}</div>
                <div className="text-sm text-gray-500 mb-4">{item.statLabel}</div>
                <h3 className="text-xl font-bold text-[#012169] mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#012169] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Improve Your Loan Approval Odds?</h2>
          <p className="text-xl text-gray-300 mb-8">
            Join thousands of satisfied customers who have successfully secured financing through BETH Finance.
          </p>
          <button 
            onClick={() => navigate('/beth-finance/signup')}
            className="bg-[#E31837] hover:bg-[#c4142f] px-10 py-4 rounded-lg font-semibold text-lg transition-all transform hover:scale-105"
          >
            Get Started Today
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-[#001a4d] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            {/* Brand */}
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-[#E31837] rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-xl">B</span>
                </div>
                <span className="text-xl font-bold">BETH Finance</span>
              </div>
              <p className="text-gray-400 text-sm">
                Helping consumers achieve their financial goals through credit enhancement solutions.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/beth-finance" className="hover:text-white transition-colors">Home</Link></li>
                <li><a href="#how-it-works" className="hover:text-white transition-colors">How It Works</a></li>
                <li><a href="#loan-types" className="hover:text-white transition-colors">Loan Types</a></li>
                <li><Link to="/beth-finance/login" className="hover:text-white transition-colors">Sign In</Link></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-bold mb-4">Legal</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
                <li><Link to="/compliance" className="hover:text-white transition-colors">Compliance</Link></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-bold mb-4">Contact Us</h4>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li className="flex items-center">
                  <Phone className="w-4 h-4 mr-2" />
                  1-800-BETH-FIN
                </li>
                <li className="flex items-center">
                  <Mail className="w-4 h-4 mr-2" />
                  support@bethfinance.com
                </li>
                <li className="flex items-start">
                  <MapPin className="w-4 h-4 mr-2 mt-1" />
                  <span>123 Finance Street<br />New York, NY 10001</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; {new Date().getFullYear()} BETH Finance. All rights reserved. A Bethereum Platform Product.</p>
            <p className="mt-2 text-xs">
              BETH Finance is a credit enhancement service. We are not a lender. Loan approval is subject to lender requirements.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BethFinanceLanding;
